<template>
  <div v-if="isLoading">
    <Spinner />
  </div>
  <div v-else>
    <Table>
      <TableHeader>
        <TableRow class="border-neutral-700">
          <TableHead>Name</TableHead>
          <TableHead>Key</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead class="text-right" />
        </TableRow>
      </TableHeader>
      <TableBody v-if="tokens.length">
        <TableRow
          v-for="token in tokens"
          :key="token.id"
          class="border-neutral-700"
        >
          <TableCell class="text-neutral-50 font-medium">{{
            token.name
          }}</TableCell>
          <TableCell class="text-neutral-50 font-medium">
            <div class="flex items-center gap-2">
              <span class="max-w-16 overflow-hidden text-ellipsis block">{{
                truncateKey(token.key)
              }}</span>
              <ClipboardList
                class="hover:cursor-pointer size-4"
                @click="copyToClipboard(token.key)"
              />
            </div>
          </TableCell>
          <TableCell class="text-neutral-50 font-medium">{{
            toDate(token.created_at)
          }}</TableCell>
          <TableCell class="text-neutral-50 font-medium">{{
            token.last_used_at
          }}</TableCell>
          <Protect :permissions="[SYS_PERMISSIONS.SYS_API_MANAGE]">
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger as-child>
                  <IconButton
                    size="small"
                    variant="ghost"
                    class="border-transparent"
                  >
                    <MoreHorizontal />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem @click="onUpdate(token.id, token.name)">
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem @click="onRemove(token.id)">
                    Remove
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </Protect>
        </TableRow>
      </TableBody>
    </Table>
  </div>
  <ApiTokenUpdateDialog
    v-model:open="updateDialogOpen"
    :token-id="updatingTokenId"
    :token-name="updatingTokenName"
  />
  <AlertDialog v-model:open="deleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
      </AlertDialogHeader>
      <div class="px-7">
        <AlertDialogDescription>
          This action cannot be undone.
        </AlertDialogDescription>
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel as-child>
          <Button variant="ghost" :disabled="isPending">Cancel</Button>
        </AlertDialogCancel>
        <Button
          variant="danger"
          :loading="isPending"
          :disabled="isPending"
          @click="onDeleteConfirmation"
        >
          Delete
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup>
import { ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { MoreHorizontal, ClipboardList } from 'lucide-vue-next';
import { useClipboard } from '@vueuse/core';
import { toast } from 'vue-sonner';
import Spinner from '@ui/components/Spinner.vue';
import Protect from '@hoc/Protect.vue';
import { SYS_PERMISSIONS } from '@/constants.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/components/table';
import requests from '@/vuex/requests.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/components/dropdown-menu';
import { IconButton } from '@ui/components/icon-button';
import { Button } from '@ui/components/button';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import { useStore } from 'vuex';
import ApiTokenUpdateDialog from './ApiTokenUpdateDialog.vue';

const queryClient = useQueryClient();

const updatingTokenName = ref('');
const updatingTokenId = ref(0);
const updateDialogOpen = ref(false);
const deleteDialogOpen = ref(false);
const deletingTokenId = ref(null);
const store = useStore();

const { isLoading, data: tokens } = useQuery({
  queryKey: ['workspaceTokens'],
  queryFn: () =>
    requests.workspaces.apiTokens.list({
      workspaceId: store.getters['workspace/getId'],
    }),
});

const { mutate: deleteToken, isPending } = useMutation({
  mutationFn: async id => {
    await requests.workspaces.apiTokens.delete({
      id,
      workspaceId: store.getters['workspace/getId'],
    });
  },
  onSuccess: async () => {
    deleteDialogOpen.value = false;
    await queryClient.invalidateQueries({ queryKey: ['workspaceTokens'] });
  },
  onError: err => {
    if (err?.response?.data?.message) {
      toast.error(err.response.data.message);
    } else {
      toast.error('Deleting the API token was unsuccessful.');
    }
  },
});

function toDate(datetime) {
  const date = new Date(datetime);
  return date.toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function onRemove(id) {
  deleteDialogOpen.value = true;
  deletingTokenId.value = id;
}

function onDeleteConfirmation() {
  deleteToken(deletingTokenId.value);
}

function onUpdate(id, name) {
  updateDialogOpen.value = true;
  updatingTokenId.value = id;
  updatingTokenName.value = name;
}

const { copy } = useClipboard();
/**
 * Copy the api token to the clipboard
 */
function copyToClipboard(key) {
  copy(key).then(() => {
    toast.success('API Token copied successfully!');
  });
}

function truncateKey(key) {
  return `${key.slice(0, 3)}...${key.slice(-4)}`;
}
</script>
